import { Divider, Grid, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import { isEmpty } from 'lodash';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useGetAccountAuthenticated } from '../../session/swr/useGetAccountAuthenticated';
import { EmailTemplateType, Language } from '../../api';
import { accountHasCourseOrderPages } from '../../utils/AccountUtils';
import { useGetEmailTemplatesParts } from '../swr/useGetEmailTemplatesParts';
import { EmailTemplatesStaticContent, resolveTemplate } from '../utils/resolveTemplate';
import { useReplaceTemplateWIthDefaults } from '../utils/replaceTemplateWIthDefaults';
import RotatingLoader from '../../common/ui/RotatingLoader';
import { EmailTemplateWrapper } from '../templateEditor/EmailTemplateWrapper';

type EditEmailTemplatesProps = {
  orderPageIdOverride?: string;
};
export const EditEmailTemplates = ({ orderPageIdOverride }: EditEmailTemplatesProps) => {
  const { account, updateLocalAccount } = useGetAccountAuthenticated({});
  const isOverrideTemplates = !!orderPageIdOverride;
  const isPassesEnabled = useFeatureFlagEnabled('feature-passes');
  const hasCourseOrderPages = accountHasCourseOrderPages(account);
  const { t } = useTranslation();
  const [selectedEmailTemplateType, setSelectedEmailTemplateType] = useState<EmailTemplateType>(
    EmailTemplateType.CONFIRMATION,
  );
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(account.customer_facing_widgets_languages[0]);
  const { emailTemplatesParts, isLoading } = useGetEmailTemplatesParts();

  const templateHtml: EmailTemplatesStaticContent | undefined = resolveTemplate(
    selectedLanguage,
    emailTemplatesParts,
    selectedEmailTemplateType,
  );
  const templateWithDefaults = useReplaceTemplateWIthDefaults({
    language: selectedLanguage,
    account,
    emailTemplatesStaticContent: templateHtml,
    orderPageIdOverride,
  });
  const handleEmailTemplateTypeChange = (event: React.MouseEvent<HTMLElement>, newOption: EmailTemplateType) => {
    if (!newOption) {
      return;
    }
    setSelectedEmailTemplateType(newOption);
  };
  const handleLanguageChange = (event: React.MouseEvent<HTMLElement>, newOption: Language) => {
    if (!newOption) {
      return;
    }
    setSelectedLanguage(newOption);
  };
  if (isLoading) {
    return (
      <Grid
        container
        sx={{
          minHeight: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <RotatingLoader />
      </Grid>
    );
  }

  return (
    <Grid container flexDirection="column">
      {!isOverrideTemplates && (
        <Grid container justifyContent="center" mb={2}>
          <Typography variant="h4">{t('emailTemplates.title')}</Typography>
        </Grid>
      )}

      <Grid container justifyContent="center">
        <ToggleButtonGroup
          size="large"
          value={selectedEmailTemplateType}
          exclusive
          onChange={handleEmailTemplateTypeChange}
          aria-label="toggle button group"
          sx={{
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 0.5,
            '& .MuiToggleButton-root': {
              borderRadius: { xs: '4px !important', sm: undefined },
              borderWidth: { xs: '1px !important', sm: undefined },
              borderStyle: { xs: 'solid !important', sm: undefined },
              flex: { xs: '1 1 40%', sm: 'unset' },
            },
          }}>
          <ToggleButton value={EmailTemplateType.CONFIRMATION} aria-label="confirmation">
            <Typography>{t('emailTemplates.confirmation')}</Typography>
          </ToggleButton>
          {hasCourseOrderPages && !isOverrideTemplates && (
            <ToggleButton value={EmailTemplateType.COURSE_CONFIRMATION} aria-label="course confirmation">
              <Typography>{t('emailTemplates.course_confirmation')}</Typography>
            </ToggleButton>
          )}
          {isPassesEnabled && !isOverrideTemplates && (
            <ToggleButton value={EmailTemplateType.PASS_CONFIRMATION} aria-label="pass confirmation">
              <Typography>{t('emailTemplates.pass_confirmation')}</Typography>
            </ToggleButton>
          )}
          <ToggleButton value={EmailTemplateType.PRE} aria-label="pre">
            <Typography>{t('emailTemplates.pre')}</Typography>
          </ToggleButton>
          <ToggleButton value={EmailTemplateType.POST} aria-label="post">
            <Typography>{t('emailTemplates.post')}</Typography>
          </ToggleButton>
          <ToggleButton value={EmailTemplateType.CANCELLATION} aria-label="post">
            <Typography>{t('emailTemplates.canellation')}</Typography>
          </ToggleButton>
          <ToggleButton value={EmailTemplateType.ABANDONEDCART} aria-label="post">
            <Typography>{t('emailTemplates.abandonedcart')}</Typography>
          </ToggleButton>
          <ToggleButton value={EmailTemplateType.VOUCHER} aria-label="voucher">
            <Typography> {t('emailTemplates.voucher')}</Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </Grid>

      {account.customer_facing_widgets_languages.length > 1 && (
        <Grid container mt={2} justifyContent="center" flexDirection="column" alignItems="center" gap={1}>
          <Grid mb={3}>
            <Divider style={{ width: '300px' }} />
          </Grid>
          <Grid item>
            <Typography variant="h5">{t('emailTemplates.language')}</Typography>
          </Grid>
          <ToggleButtonGroup
            value={selectedLanguage}
            exclusive
            onChange={handleLanguageChange}
            aria-label="toggle button group">
            {account.customer_facing_widgets_languages.map((lang: Language) => (
              <ToggleButton key={lang} value={lang} aria-label="confirmation">
                <Typography>{t(`emailTemplates.${lang}`)}</Typography>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Grid>
      )}

      {isEmpty(emailTemplatesParts) ? (
        <Grid
          container
          sx={{
            minHeight: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <RotatingLoader />
        </Grid>
      ) : (
        <Grid container justifyContent="center" mt={2}>
          <EmailTemplateWrapper
            account={account}
            updateLocalAccount={updateLocalAccount}
            selectedLanguage={selectedLanguage}
            templateType={selectedEmailTemplateType}
            templateWithDefaults={templateWithDefaults}
            orderPageIdOverride={orderPageIdOverride}
          />
        </Grid>
      )}
    </Grid>
  );
};
